import { TOUJOU_EVENT_CONTACT_DIALOG_OPEN } from '../utils/constants';

/**
 * Handles behavior for the contact dialog component.
 * - Opens when TOUJOU_EVENT_CONTACT_DIALOG_OPEN is dispatched.
 * - Closes on outside click or close button.
 */
class ContactDialog {
  selectors = {
    focusableEls: '.contact-dialog__close, a.contact-dialog__link',
  };

  /** @property({ type: HTMLElement }) */
  _focusableEls = [];

  /** @property({ type: HTMLElement }) */
  _firstFocusableEl = null;

  /** @property({ type: HTMLElement }) */
  _lastFocusableEl = null;

  /**
   * @param {HTMLDialogElement} dialogEl - The dialog element to manage.
   */
  constructor(dialogEl) {
    /** @type {HTMLDialogElement} */
    this.dialogEl = dialogEl;

    this._focusableEls = [...this.dialogEl.querySelectorAll(this.selectors.focusableEls)];
    this._firstFocusableEl = this._focusableEls.at(0);
    this._lastFocusableEl = this._focusableEls.at(this._focusableEls.length - 1);

    /** @type {HTMLButtonElement|null} */
    this.closeButton = this.dialogEl.querySelector('.contact-dialog__close');

    this._onDialogClick = this._onDialogClick.bind(this);
    this._openDialog = this._openDialog.bind(this);
    this._closeDialog = this._closeDialog.bind(this);

    this._addEventListeners();
  }

  /**
   * Adds all relevant event listeners
   * @private
   */
  _addEventListeners() {
    window.addEventListener(TOUJOU_EVENT_CONTACT_DIALOG_OPEN, this._openDialog);
    this.closeButton?.addEventListener('click', this._closeDialog);
    this.dialogEl.addEventListener('click', this._onDialogClick);
  }

  /**
   * Opens the dialog when the custom event is triggered.
   * @private
   */
  _openDialog() {
    this.dialogEl.showModal();
    window.addEventListener('keydown', this._handleKeyDown.bind(this));
  }

  /**
   * Closes the dialog.
   * @private
   */
  _closeDialog() {
    this.dialogEl.close();
    window.removeEventListener('keydown', this._handleKeyDown);
  }

  /**
   * Closes the dialog if the user clicks outside the dialog card bounding box.
   * @param {MouseEvent} event
   * @private
   */
  _onDialogClick(event) {
    const dialogCardRect = this.dialogEl.getBoundingClientRect();
    const clickInsideDialog = (
      event.clientX >= dialogCardRect.left
      && event.clientX <= dialogCardRect.right
      && event.clientY >= dialogCardRect.top
      && event.clientY <= dialogCardRect.bottom
    );

    if (!clickInsideDialog) {
      this._closeDialog();
    }
  }

  /**
   * React to key down events
   *  - Esc key should close the contact box
   *  - tab should move to next focusable element
   *  - tab + shift should move to previous focusable element
   * @param {KeyboardEvent} event
   */
  _handleKeyDown(event) {
    if (event.key === 'Escape') {
      this._handleEscapePress();
    } else if (event.key === 'Tab') {
      this._handleTabPress(event);
    }
  }

  /**
   * Close the contact box when the escape key is pressed
   */
  _handleEscapePress() {
    this._closeDialog();
  }

  /**
   * Focus trap inside the contact box
   * - tab should move to next focusable element
   * - tab + shift should move to previous focusable element
   * @param {KeyboardEvent} event
   */
  _handleTabPress(event) {
    if (event.shiftKey) {
      if (document.activeElement === this._firstFocusableEl) {
        event.preventDefault();
        this._lastFocusableEl?.focus();
      }
    } else if (document.activeElement === this._lastFocusableEl) {
      event.preventDefault();
      this._firstFocusableEl?.focus();
    }
  }
}

/**
 * Initializes the contact dialog if it exists on the page.
 * Prevents multiple initializations via a custom instance property.
 */
function initContactDialog() {
  /** @type {HTMLDialogElement|null} */
  const contactDialog = document.querySelector('#contact-dialog');
  if (!contactDialog) return;

  if (!contactDialog.contactDialogInstance) {
    contactDialog.contactDialogInstance = new ContactDialog(contactDialog);
  }
}

if (document.readyState !== 'loading') {
  setTimeout(initContactDialog);
} else {
  document.addEventListener('DOMContentLoaded', initContactDialog, { once: true });
}
